import { useState, useRef, useEffect, useCallback } from 'react';
import { useNotifications, isAbortError, PatientStatus } from '@medprompt/shared';
import { hasPatientCodesData } from './PatientDiaryPanel';

const CONCERNING_PATIENT_STATUSES = [PatientStatus.DEDUCTION_PENDING, PatientStatus.INACTIVE];

const useDocumentPatient = ({ documentId, doc, documentService, docScopeOptions, loadKey }) => {
  const { addNotification, addReferralUpdateCallback } = useNotifications();

  const [patientData, setPatientData] = useState(null);
  const [patientDataError, setPatientDataError] = useState(false);
  const [patientRecordData, setPatientRecordData] = useState(null);
  const [patientRecordDataError, setPatientRecordDataError] = useState(false);
  const [refreshingPatientCodes, setRefreshingPatientCodes] = useState(false);
  const refreshingPatientCodesRef = useRef(false);
  const [showPendingReferralsModal, setShowPendingReferralsModal] = useState(false);
  const [showPatientStatusWarningModal, setShowPatientStatusWarningModal] = useState(false);
  // Show each proactive modal only once per document per session
  const pendingReferralsShownRef = useRef(new Set());
  const patientStatusShownRef = useRef(new Set());

  // Separate generation for patient vs patient-record fetches. Doc-scoped abort is shared with
  // comms/duplicates; patient identity can change on the same document (NHS rematch) without a
  // new doc scope, and the two patient endpoints can complete in either order.
  const patientScopeIdRef = useRef(0);

  const docId = doc?.documentId;
  const docPatientId = doc?.patientId;
  const docMatchesUrl = Boolean(docId && docId === documentId);

  const docPatientIdRef = useRef(null);
  useEffect(() => {
    docPatientIdRef.current = docPatientId || null;
  }, [docPatientId]);

  // Referral events are clinic-wide, so refetch the patient record only when the referral is one
  // this document shows, or is newly created and so might belong here.
  const pendingReferralIdsRef = useRef(new Set());
  useEffect(() => {
    pendingReferralIdsRef.current = new Set(
      (patientRecordData?.pendingReferrals || []).map(referral => referral.referralId)
    );
  }, [patientRecordData]);

  const resetPatientState = useCallback(() => {
    setPatientData(null);
    setPatientDataError(false);
    setPatientRecordData(null);
    setPatientRecordDataError(false);
    setRefreshingPatientCodes(false);
    refreshingPatientCodesRef.current = false;
  }, []);

  useEffect(() => {
    resetPatientState();
    setShowPendingReferralsModal(false);
    setShowPatientStatusWarningModal(false);
  }, [loadKey, resetPatientState]);

  // requestId is captured against patientScopeIdRef so a response for a stale document or
  // patient rematch (e.g. still in flight when the user auto-navigates) is dropped.
  const fetchPatientData = useCallback(async (targetDocumentId, requestId, options) => {
    try {
      setPatientDataError(false);
      const patient = await documentService.fetchPatientData(targetDocumentId, options);
      if (requestId !== patientScopeIdRef.current) {
        return;
      }
      setPatientData(patient);
    } catch (error) {
      if (isAbortError(error) || requestId !== patientScopeIdRef.current) return;
      console.error('Failed to fetch patient data:', error);
      setPatientData(null);
      setPatientDataError(true);
    }
  }, [documentService]);

  // Guarded by requestId the same way as fetchPatientData.
  const fetchPatientRecordData = useCallback(async (targetDocumentId, requestId, options) => {
    try {
      setPatientRecordDataError(false);
      const patientRecord = await documentService.fetchPatientRecordData(targetDocumentId, options);
      if (requestId !== patientScopeIdRef.current) {
        return;
      }
      setPatientRecordData(patientRecord);
    } catch (error) {
      if (isAbortError(error) || requestId !== patientScopeIdRef.current) return;
      console.error('Failed to fetch patient record data:', error);
      setPatientRecordData(null);
      setPatientRecordDataError(true);
    }
  }, [documentService]);

  // Keyed by URL documentId as well as doc identity so Next/Prev abort the previous patient's
  // in-flight demographics + record before the previous `doc` state is cleared.
  useEffect(() => {
    const requestId = ++patientScopeIdRef.current;
    const controller = new AbortController();
    const options = { signal: controller.signal };

    resetPatientState();
    if (docMatchesUrl && docPatientId) {
      fetchPatientData(docId, requestId, options);
      fetchPatientRecordData(docId, requestId, options);
    }

    return () => controller.abort();
  }, [documentId, docId, docPatientId, docMatchesUrl, fetchPatientData, fetchPatientRecordData, resetPatientState]);

  const handleRefreshPatientCodes = useCallback(async () => {
    if (!documentId || refreshingPatientCodesRef.current) return;
    const requestId = patientScopeIdRef.current;
    refreshingPatientCodesRef.current = true;
    setRefreshingPatientCodes(true);
    try {
      const codes = await documentService.fetchFreshPatientCodes(documentId);
      if (requestId !== patientScopeIdRef.current) return;
      if (!hasPatientCodesData(codes)) {
        addNotification(`patient-codes-refresh-empty-${documentId}`, 'Could not refresh problems from EMIS', { isError: true });
        return;
      }
      setPatientRecordData(previous => (
        previous ? { ...previous, patientCodesData: codes } : previous
      ));
    } catch (error) {
      if (requestId !== patientScopeIdRef.current) return;
      console.error('Failed to refresh patient codes:', error);
      addNotification(`patient-codes-refresh-error-${documentId}`, 'Failed to refresh problems from EMIS', { isError: true });
    } finally {
      refreshingPatientCodesRef.current = false;
      if (requestId === patientScopeIdRef.current) {
        setRefreshingPatientCodes(false);
      }
    }
  }, [documentId, documentService, addNotification]);

  /** Reconnect catch-up: refetch the patient for the current scope if the document has one. */
  const refetchPatient = useCallback(() => {
    if (!docPatientIdRef.current) return;
    const requestId = patientScopeIdRef.current;
    fetchPatientData(documentId, requestId, docScopeOptions());
    fetchPatientRecordData(documentId, requestId, docScopeOptions());
  }, [documentId, fetchPatientData, fetchPatientRecordData, docScopeOptions]);

  useEffect(() => {
    if (!addReferralUpdateCallback || !docMatchesUrl) return;

    const handleReferralUpdate = (referralId, statusTransition) => {
      const isShownHere = referralId && pendingReferralIdsRef.current.has(referralId);
      const isNewReferral = statusTransition?.from === 'NON_EXISTENT';
      if (!isShownHere && !isNewReferral) {
        return;
      }
      fetchPatientRecordData(docId, patientScopeIdRef.current, docScopeOptions());
    };

    return addReferralUpdateCallback(handleReferralUpdate);
  }, [addReferralUpdateCallback, docId, docMatchesUrl, fetchPatientRecordData, docScopeOptions]);

  // Show proactive pending referrals modal once per document per session when data arrives
  useEffect(() => {
    if (!patientRecordData?.pendingReferrals?.length || !docMatchesUrl) return;
    if (pendingReferralsShownRef.current.has(docId)) return;
    pendingReferralsShownRef.current.add(docId);
    setShowPendingReferralsModal(true);
  }, [patientRecordData, docId, docMatchesUrl]);

  // Show patient status warning once per document when the patient is DEDUCTION_PENDING or INACTIVE
  const patientStatus = patientData ? (patientData.status || PatientStatus.ACTIVE) : null;
  useEffect(() => {
    if (!patientStatus || !docMatchesUrl) return;
    if (!CONCERNING_PATIENT_STATUSES.includes(patientStatus)) return;
    if (patientStatusShownRef.current.has(docId)) return;
    patientStatusShownRef.current.add(docId);
    setShowPatientStatusWarningModal(true);
  }, [patientStatus, docId, docMatchesUrl]);

  const dismissPendingReferrals = useCallback(() => setShowPendingReferralsModal(false), []);
  const dismissPatientStatusWarning = useCallback(() => setShowPatientStatusWarningModal(false), []);

  // The diary badge and panel read patientRecordData.pendingReferrals, so completing or deleting
  // has to drop the referral locally rather than wait for a patient-record refresh.
  const removePendingReferralLocally = useCallback((referralId) => {
    setPatientRecordData(previous => (
      previous?.pendingReferrals
        ? {
            ...previous,
            pendingReferrals: previous.pendingReferrals.filter(
              referral => referral.referralId !== referralId
            )
          }
        : previous
    ));
  }, []);

  const applyPendingReferralPatch = useCallback((referralId, fields) => {
    setPatientRecordData(previous => (
      previous?.pendingReferrals
        ? {
            ...previous,
            pendingReferrals: previous.pendingReferrals.map(referral => (
              referral.referralId === referralId ? { ...referral, ...fields } : referral
            ))
          }
        : previous
    ));
  }, []);

  return {
    patientData,
    patientDataError,
    patientRecordData,
    patientRecordDataError,
    pendingReferralsCount: patientRecordData?.pendingReferrals?.length || 0,
    refreshingPatientCodes,
    handleRefreshPatientCodes,
    refetchPatient,
    showPendingReferralsModal,
    dismissPendingReferrals,
    showPatientStatusWarningModal,
    dismissPatientStatusWarning,
    removePendingReferralLocally,
    applyPendingReferralPatch,
  };
};

export default useDocumentPatient;
